import * as THREE from 'three';
import { applyH } from '../core/linalg.js';
import { activeCam, frameOrbit, frameSelection, isSiteLike, orb } from '../scene/cameras.js';
import { flatTarget, panFlatByPixels, setFlatZoom } from './flat-view.js';
import { content, cornerCam, flatCam, orbitCam } from '../scene/context.js';
import { AX, GIZMO_TARGET_PX, gizmo, gizmoWorldSize, gzGroups, objQuat, pickRotateAxis, setHover, updateGizmo, worldDeltaToLocal, worldPose } from '../scene/gizmo.js';
import { eyeCam, eyePicker, eyeVec, updateEyeHelpers } from '../scene/helpers.js';
import { deleteSel, duplicateSel, getSel, select, syncTransform } from '../scene/objects.js';
import { emit, requestRender } from '../state/bus.js';
import { pushHistory, pushHistorySoon, redo, undo } from '../state/history.js';
import { captureKey, togglePlay } from '../state/timeline.js';
import { $, S, clamp, fieldOwnsKey, typingTarget } from '../state/store.js';
import { mask, maskPointer } from '../ui/mask-editor.js';
import { refreshInspectorValues, renderRails, setGizmoMode } from '../ui/rails.js';
import { applySiteTransform, setSiteZoom, siteCornerDim, siteCornerH, siteCornerInv, siteDim, siteH, siteInv, sitePanelAt } from '../ui/site-view.js';
import { toast } from '../ui/toast.js';
import { canUseMotion } from '../services/limits.js';
import { hasKeySelection } from '../ui/timeline-view.js';

/* ═══════════════════════════════════════════════════════════
   VIEWPORT INTERACTION
   ═══════════════════════════════════════════════════════════ */
export const ray = new THREE.Raycaster();
const ndc = new THREE.Vector2();
export let drag = null, orbiting = null, gzDrag = null, eyeDrag = null;

/* Which camera a pointer event belongs to. #fitbox is reused for standalone
   Orbit/Flat and Split's left pane (see ui/chrome.js's setView), so
   activeCam() — which already treats 'split' as orbit-like, see
   isOrbitLike in scene/cameras.js — resolves it correctly there. Split's
   separate right pane (#fitboxSplitR) is always flatCam regardless of
   S.view, since that pane only ever shows the Flat deliverable preview. */
/* DID THIS POINTER LAND ON THE MOCKUP? In Split the orbit pane is live at the
   same moment the mockup is, so isSiteLike() — "a mockup is on screen" — is no
   longer enough to claim an event for it. The surface the handler was bound to
   is: #siteWrap carries its own listeners and moves into the pane with them,
   which is the same test camFor already makes for #fitboxSplitR just below. */
const onSite = ev => isSiteLike() && !!ev && ev.currentTarget === $('#siteWrap');

function camFor(ev){
  if(ev.currentTarget === $('#fitboxSplitR')) return flatCam;
  /* On the mockup, the camera follows the panel under the pointer — the same
     one pointerNDC just mapped through, so the ray and the projection agree.
     Returned explicitly rather than falling through to activeCam(), which
     answers orbitCam for 'split' and would raycast the right pane through the
     left pane's camera. */
  if(onSite(ev)){
    if(S.cornerPanel){
      const s = sitePanel(ev.clientX, ev.clientY);
      if(s && s.right) return cornerCam;
    }
    return flatCam;
  }
  // a corner screen's second panel has its own frustum, and its pane is a
  // fully interactive surface — the gizmo maths is camera-generic, so
  // dragging there moves the object in world space with no special case
  if(ev.currentTarget === $('#fitboxCornerR')) return cornerCam;
  return activeCam();
}
/* Normalised device coordinates for the pointer, whichever surface it is
   over. `override` lets a caller that isn't one of the per-surface
   listeners below — a mask-point drag continuing on a window-level
   pointermove after the gesture has left its origin element, see
   mask-editor.js — supply the camera/element it belongs to directly
   instead of trying to read it off ev.currentTarget (which would be
   window, not any real surface, from there). */
/* Which panel's warp a point on the photo goes through. On a corner screen
   both panels are composited onto the same photograph, each by its own
   homography, so mapping a pointer through the wrong one lands it somewhere
   plausible and wrong — the exact failure this codebase has already fixed
   twice. Resolved by a point-in-quad test against the MARKED quads, which are
   what the person is actually looking at. */
function sitePanel(clientX, clientY){
  const im = $('#siteImg');
  const r = im.getBoundingClientRect();
  if(!r.width || !r.height) return null;
  const k = r.width / Math.max(1, im.clientWidth);
  const dx = (clientX - r.left)/k, dy = (clientY - r.top)/k;
  const right = S.cornerPanel && siteCornerInv && sitePanelAt(dx, dy) === 'right';
  return {
    r, k, dx, dy, right,
    inv: right ? siteCornerInv : siteInv,
    H:   right ? siteCornerH   : siteH,
    dim: right ? siteCornerDim : siteDim
  };
}

function pointerNDC(ev, override){
  if(onSite(ev) && !(override && override.el)){
    if(!siteInv) return null;
    const s = sitePanel(ev.clientX, ev.clientY);
    if(!s || !s.inv) return null;
    // the rect includes the zoom transform; the homography is in layout pixels
    const p = applyH(s.inv, s.dx, s.dy);
    if(!isFinite(p[0]) || !isFinite(p[1])) return null;
    return {x:(p[0]/s.dim.w)*2-1, y:-(p[1]/s.dim.h)*2+1};
  }
  const el = (override && override.el) || ev.currentTarget;
  const r = el.getBoundingClientRect();
  if(!r.width || !r.height) return null;
  return {x:((ev.clientX-r.left)/r.width)*2-1, y:-((ev.clientY-r.top)/r.height)*2+1};
}
export function setRay(ev, override){
  const n = pointerNDC(ev, override);
  if(!n) return false;
  ndc.set(n.x, n.y);
  ray.setFromCamera(ndc, (override && override.cam) || camFor(ev));
  return true;
}
export function pick(ev){
  if(!setRay(ev)) return null;
  const hits = ray.intersectObjects(content.children, true);
  for(const h of hits){
    let n = h.object;
    while(n && n.userData.objId===undefined) n = n.parent;
    if(n) return {obj:S.objects.find(o=>o.id===n.userData.objId), point:h.point};
  }
  return null;
}
/** Where an NDC point lands in client pixels — the exact inverse of
 *  pointerNDC, so the two can never disagree about where something is. */
function ndcToClient(nx, ny, ev){
  if(ev ? onSite(ev) : S.view === 'site'){
    /* On-site draws the canvas through a CSS matrix3d homography, so a point
       does NOT land where a plain bounding rect would put it. Mapping it
       forward through siteH is the mirror of pointerNDC's siteInv. */
    if(!siteH) return null;
    /* Forward through the SAME panel's warp the pointer came back through, or
       the gizmo's safe area would be measured against the other panel — see
       sitePanel above. */
    const s = sitePanel(ev.clientX, ev.clientY);
    if(!s || !s.H) return null;
    const p = applyH(s.H, (nx*0.5+0.5)*s.dim.w, (1-(ny*0.5+0.5))*s.dim.h);
    if(!isFinite(p[0]) || !isFinite(p[1])) return null;
    return { x: s.r.left + p[0]*s.k, y: s.r.top + p[1]*s.k };
  }
  const el = ev.currentTarget;
  const r = el.getBoundingClientRect();
  if(!r.width || !r.height) return null;
  return { x: r.left + (nx*0.5+0.5)*r.width, y: r.top + (1-(ny*0.5+0.5))*r.height };
}

/* Screen-pixel distance from a pointer event to the gizmo's own on-screen
   position — Infinity if there's nothing to measure against. Used to give the
   selected object a "safe area" around its gizmo (see onDown): two objects
   sitting close together, or one partly in front of another, made it too easy
   to grab the wrong one right next to the gizmo you were actually aiming for,
   since a plain raycast always picks whatever's closest to the camera at that
   exact pixel, not whatever's selected.

   It used to map the gizmo with a plain bounding rect, which is right in Orbit
   and Flat and WRONG in On-site: the warp means the gizmo's true screen
   position can be a long way from where the rect maths puts it, and the error
   grows with the severity of the perspective. Since this distance is what
   grants the safe area, getting it wrong put that area somewhere other than
   the gizmo — so with two objects close together the protection landed on
   empty photo and the wrong object won the pick. Exactly the case it exists
   for, failing exactly where it was needed. */
function gizmoScreenDist(ev){
  if(!gizmo.visible) return Infinity;
  const p = gizmo.position.clone().project(camFor(ev));
  const c = ndcToClient(p.x, p.y, ev);
  if(!c) return Infinity;
  return Math.hypot(ev.clientX-c.x, ev.clientY-c.y);
}
// Same fixed pixel target gizmoWorldSize (scene/gizmo.js) sizes the gizmo
// itself by, so the safe area tracks whatever S.gizmoSize the gizmo is
// currently drawn at rather than drifting out of sync with it.
function gizmoSafeRadiusPx(){
  return GIZMO_TARGET_PX * S.gizmoSize;
}
function pickGizmo(ev){
  if(!gizmo.visible) return null;
  // Select has no handle group of its own — gzGroups[S.gizmo] would be
  // undefined below, and updateGizmo already hides every real group while
  // it's active (none of them match S.gizmo), so there's nothing to pick.
  if(S.gizmo === 'select') return null;
  const cam = camFor(ev);
  /* Split renders both panes every frame, ending on the orbit pass — so
     between frames the gizmo's position/scale reflect whichever camera
     rendered last, not necessarily this event's. Refresh it before
     hit-testing; the next render recomputes it again regardless.

     Refreshed for the camera the gizmo was DRAWN through, which On-site is
     not the same as the one the pointer is mapped through. There, one
     updateGizmo(activeCam()) sizes the gizmo and scene/renderer.js then
     renders that single transform through BOTH panels — the second pass
     exists to draw the far half of one gizmo, not a second gizmo. But
     camFor returns cornerCam for a pointer over the second panel, and
     gizmoWorldSize is camera-dependent, so re-sizing here made the pick
     test a gizmo a different size from the one on screen: the arrows were
     visible and simply not where the ray was looking. Only for handles
     whose pick is 3D — the rotate rings project their own silhouette and
     never cared.

     The RAY still uses the pointer's own camera. That is what keeps the
     mapping honest; what has to match the screen is the thing being aimed
     at. Same failure this file already guards against twice: drawn through
     one frustum, picked through another. */
  updateGizmo(onSite(ev) ? flatCam : cam);
  if(!setRay(ev)) return null;
  // Rotate rings overlap heavily both on screen and in raycast depth order —
  // picked by 2D screen-space proximity instead. See pickRotateAxis's comment.
  if(S.gizmo === 'rotate'){
    const node = pickRotateAxis(ndc.x, ndc.y, cam);
    return node ? {gz:node.userData.gz, node} : null;
  }
  const hits = ray.intersectObjects([gzGroups[S.gizmo]], true);
  // An axis handle viewed nearly end-on collapses to a small disk right at
  // the gizmo's own origin — exactly where the free-move sphere (or a plane
  // handle) sits too — and it raycasts as the CLOSEST hit there since its
  // near end starts at that same origin. Flat and On-site hit this on every
  // single session, not as an edge case: their camera is always aligned
  // exactly along world Z by the off-axis design (scene/cameras.js's
  // updateFlatCam), so the Z move/scale handle is ALWAYS end-on in those
  // views — a click aimed at the centre grabbed it instead of the sphere,
  // and dragging it sideways silently did nothing (axisParam's own "axis
  // lies along the viewing ray" fallback only reads vertical pointer travel
  // for exactly this case). Skipping a degenerate axis handle here — the
  // same threshold axisParam already uses — lets the pick fall through to
  // whatever's actually usable underneath: the sphere, a plane handle, or
  // nothing (which reaches onDown's own safe-area body-drag instead, which
  // already supports Shift for depth). The handle isn't lost — it is just
  // never the automatic winner over something you can actually drag.
  const eyeDir = gizmo.position.clone().sub(cam.position).normalize();
  for(const h of hits){
    let n = h.object;
    while(n && !n.userData.gz) n = n.parent;
    if(!n) continue;
    const gz = n.userData.gz;
    if(gz.kind === 'axis'){
      const axisW = AX[gz.axis].clone().applyQuaternion(gizmo.quaternion).normalize();
      const perp = eyeDir.clone().sub(axisW.clone().multiplyScalar(eyeDir.dot(axisW)));
      if(perp.lengthSq() < 2.5e-3) continue;
    }
    return {gz, node:n};
  }
  return null;
}
/* A ray that STARTS exactly on the plane it's being tested against
   "intersects" it trivially at its own origin, for any direction that
   isn't parallel to the plane — mathematically correct (a line through a
   point on a plane, not parallel to it, only crosses that plane at that
   one point), but useless here: every screen position then maps to the
   same degenerate point, so a plane- or rotate-handle drag silently does
   nothing. This is common, not a corner case — a manual/centred viewer
   sits at y=0 (manualSetup), and a freshly added object defaults to y=0
   too, so the Y-normal plane handle hits this on essentially every
   Flat/Split drag until the object or the viewer moves off that line.
   Falls back to mapping the pointer's screen position directly onto the
   plane's own basis instead, the same trick axisParam's own degenerate
   case (the axis lying along the view ray) already uses for a line. */
function planeFallback(ev, plane){
  const p = pointerNDC(ev);
  if(!p) return null;
  const n = plane.normal;
  const up = Math.abs(n.y) < .9 ? new THREE.Vector3(0,1,0) : new THREE.Vector3(1,0,0);
  const u = new THREE.Vector3().crossVectors(up, n).normalize();
  const v = new THREE.Vector3().crossVectors(n, u).normalize();
  const origin = plane.coplanarPoint(new THREE.Vector3());
  const scale = gizmoWorldSize(camFor(ev), origin) * 3;
  return origin.addScaledVector(u, p.x*scale).addScaledVector(v, p.y*scale);
}
function planeHit(ev, plane){
  if(!setRay(ev)) return null;
  if(Math.abs(plane.distanceToPoint(ray.ray.origin)) < 1e-4) return planeFallback(ev, plane);
  const p = new THREE.Vector3();
  return ray.ray.intersectPlane(plane, p) ? p : null;
}
function planeAt(ev, normal, point){
  return planeHit(ev, new THREE.Plane().setFromNormalAndCoplanarPoint(normal, point));
}

/* Distance along `axis` of the pointer ray, measured on the plane that contains
   the axis and faces the viewer as squarely as it can.

   The measurement must use the viewing ray *at the object*, not the camera's
   optical axis. The off-axis frustum keeps that axis pinned to -Z and shears
   the projection instead, so camera-forward is exactly parallel to the Z handle
   in Flat and On-site view no matter where the eye sits. The eye-to-object ray
   is off-axis whenever the eye is, which is the whole point of this tool. */
function axisParam(ev, origin, axis, cam){
  const eye = origin.clone().sub(cam.position).normalize();
  const n = eye.clone().sub(axis.clone().multiplyScalar(eye.dot(axis)));
  if(n.lengthSq() < 2.5e-3){
    // Axis lies along the viewing ray, so no geometric solve exists. Map
    // vertical pointer travel onto it instead: drag up to pull toward the
    // viewer. Less exact, but depth never becomes unreachable.
    const p = pointerNDC(ev);
    return p ? p.y * gizmoWorldSize(cam, origin) * 3 : null;
  }
  const hit = planeAt(ev, n.normalize(), origin);
  return hit ? hit.sub(origin).dot(axis) : null;
}
/* Unit vector from the gizmo centre to the pointer, inside the ring's plane. */
function radialAt(ev, axis, origin){
  const hit = planeAt(ev, axis, origin);
  if(!hit) return null;
  const v = hit.sub(origin);
  v.sub(axis.clone().multiplyScalar(v.dot(axis)));
  return v.lengthSq() < 1e-10 ? null : v.normalize();
}
function screenDist(ev, origin, cam){
  const n = pointerNDC(ev); if(!n) return null;
  const p = origin.clone().project(cam);
  const d = Math.hypot(n.x - p.x, n.y - p.y);
  return d < .02 ? null : d;
}

function startGizmo(ev, hit){
  const o = getSel();
  // The eye/camera "selected" instead of a real object (see
  // scene/gizmo.js's updateGizmo, which only ever shows move handles for
  // it) — same math throughout, just against eyeVec()/S.nudge instead of
  // a real object's pos/scale/rotation, which it has neither of.
  const eyeMode = !o && S.eyeSelected;
  if(!o && !eyeMode) return null;
  const gz = hit.gz, cam = camFor(ev);
  const origin = eyeMode ? eyeVec() : worldPose(o).pos;
  const axisW = gz.axis ? AX[gz.axis].clone().applyQuaternion(gizmo.quaternion).normalize() : null;
  const st = {gz, node:hit.node, o, eyeMode, cam, origin, axisW,
    pos0: eyeMode ? {...S.nudge} : {...o.pos}, scl0: eyeMode ? null : {...o.scale}, q0: eyeMode ? null : objQuat(o), acc:0};

  if(gz.mode==='move'){
    if(gz.kind==='axis'){ st.t0 = axisParam(ev, origin, axisW, cam); if(st.t0==null) return null; }
    else{
      st.n = gz.kind==='plane' ? axisW
           : new THREE.Vector3(0,0,-1).applyQuaternion(cam.quaternion);
      st.p0 = planeAt(ev, st.n, origin); if(!st.p0) return null;
    }
  }else if(gz.mode==='rotate'){
    st.v0 = radialAt(ev, axisW, origin); if(!st.v0) return null;
  }else{
    if(gz.kind==='axis'){
      st.t0 = axisParam(ev, origin, axisW, cam);
      if(st.t0==null || Math.abs(st.t0) < 1e-5) return null;
    }else{
      st.d0 = screenDist(ev, origin, cam); if(!st.d0) return null;
    }
  }
  return st;
}

/* Move stays 1:1 with the cursor — the point you grabbed has to stay under
   the pointer or dragging reads as disconnected, not smooth. Rotate and
   Scale don't carry that same expectation (you're sweeping an angle/
   distance around a pivot, not tracking one grabbed point), and full 1:1
   angular tracking in particular reads as twitchy whenever the grab point
   ends up close to the gizmo's screen-space centre — a small mouse move
   there is a huge swept angle. Toned down by default; Shift's existing
   .22 "fine" multiplier still applies on top of whichever base this picks. */
const BASE_SENS = {move:1, rotate:.5, scale:.6};
/* Which track a drag in each mode writes to. The gizmo has always known which
   of the three it is moving; it just used to keyframe all of them. */
const GZ_PROP = {move:'pos', rotate:'rot', scale:'scale'};
function moveGizmo(ev, st){
  const {gz, o} = st;
  const fine = ev.shiftKey ? .22 : BASE_SENS[gz.mode];
  const snap = ev.ctrlKey || ev.metaKey;

  if(gz.mode==='move'){
    const d = new THREE.Vector3();
    if(gz.kind==='axis'){
      const t = axisParam(ev, st.origin, st.axisW, st.cam);
      if(t==null) return;
      d.copy(st.axisW).multiplyScalar((t - st.t0)*fine);
    }else{
      const p = planeAt(ev, st.n, st.origin);
      if(!p) return;
      d.copy(p).sub(st.p0).multiplyScalar(fine);
    }
    // Rotate/scale never reach here for the eye — updateGizmo only ever
    // shows move handles for it (see scene/gizmo.js) — so o is only null
    // in this one branch, and only when st.eyeMode is true.
    const target = st.eyeMode ? S.nudge : o.pos;
    /* d is a WORLD movement; a linked object's position is in its master's
       frame (see scene/gizmo.js's worldPose), so it is brought across first.
       The identity for everything that is not linked. */
    const dl = st.eyeMode ? d : worldDeltaToLocal(o, d);
    target.x = st.pos0.x + dl.x;
    target.y = st.pos0.y + dl.y;
    target.z = st.pos0.z + dl.z;
    if(snap){ const q=.05; ['x','y','z'].forEach(k=>target[k]=Math.round(target[k]/q)*q); }

  }else if(gz.mode==='rotate'){
    const v = radialAt(ev, st.axisW, st.origin);
    if(!v) return;
    const raw = Math.atan2(new THREE.Vector3().crossVectors(st.v0, v).dot(st.axisW), st.v0.dot(v));
    let da = raw - st.acc;                      // unwrap so it keeps turning past ±180°
    while(da >  Math.PI) da -= Math.PI*2;
    while(da < -Math.PI) da += Math.PI*2;
    st.acc += da;
    let ang = st.acc*fine;
    if(snap){ const q = Math.PI/36; ang = Math.round(ang/q)*q; }   // 5°
    /* The turn is about a WORLD axis, so it is applied to the object's world
       orientation and brought back into its master's frame: P⁻¹ · R · P · q. */
    const P = worldPose(o).parentQuat;
    const e = new THREE.Euler().setFromQuaternion(
      P.clone().invert().multiply(new THREE.Quaternion().setFromAxisAngle(st.axisW, ang)).multiply(P).multiply(st.q0), 'XYZ');
    o.rot.x = e.x*180/Math.PI; o.rot.y = e.y*180/Math.PI; o.rot.z = e.z*180/Math.PI;

  }else{
    let k;
    if(gz.kind==='axis'){
      const t = axisParam(ev, st.origin, st.axisW, st.cam);
      if(t==null) return;
      k = t/st.t0;
    }else{
      const d = screenDist(ev, st.origin, st.cam);
      if(!d) return;
      k = d/st.d0;
    }
    k = 1 + (k-1)*fine;
    if(snap) k = Math.round(k/.1)*.1;
    k = Math.max(.01, k);
    if(gz.kind==='axis' && !o.lockScale){
      o.scale[gz.axis] = Math.max(.01, st.scl0[gz.axis]*k);
    }else{
      ['x','y','z'].forEach(a=>o.scale[a] = Math.max(.01, st.scl0[a]*k));
    }
  }
  if(st.eyeMode) updateEyeHelpers();
  else{
    /* Before the sync, not after: on an animated property the value on the
       object is about to be re-derived from the track, so the drag has to be
       recorded as a keyframe first or the next evaluation throws it away.
       No-op when nothing is animated. */
    captureKey(o, GZ_PROP[gz.mode]);
    syncTransform(o);
  }
  refreshInspectorValues();
  requestRender();
}

function onDown(ev){
  if(S.stage==='calib') return;
  if(mask.active){ if(maskPointer(ev)){ ev.stopPropagation(); return; } }
  try{ ev.currentTarget.setPointerCapture(ev.pointerId); }catch(e){}

  if(ev.button===0){
    const gh = pickGizmo(ev);
    if(gh){
      const st = startGizmo(ev, gh);
      if(st){ gzDrag = st; setHover(gh.node); requestRender(); return; }
      toast('That handle is edge-on from here. Orbit round a little and try again.', true);
      return;
    }
  }

  /* Dragging the eye helper directly — only in Orbit, and only for a solved
     (photo-calibrated) viewpoint: eyeCam.visible is already exactly that
     condition (see scene/helpers.js's updateEyeHelpers), since a manual
     setup's own Camera object draws its body there instead and already has
     this via the regular object-drag path below. S.showHelpers is checked
     separately — eyeCam's own .visible flag doesn't follow its parent
     `helpers` group's, and raycasting ignores ancestor visibility, so a
     hidden-helpers scene would otherwise still let this fire on nothing
     the user can see. Same screen-plane-drag / Shift-for-depth convention
     as any other object's body-drag below, just moving S.nudge instead of
     a real object's own position — the underlying solved S.eye is never
     touched, so this can never leave the calibration needing a re-lock. */
  if(ev.button===0 && S.showHelpers && eyeCam.visible && camFor(ev)===orbitCam && setRay(ev)){
    const eyeHit = ray.intersectObject(eyePicker, true);
    if(eyeHit.length){
      // Selects it, the same way clicking a real object's body both
      // selects it and (in move mode) starts a body-drag — the eye's own
      // gizmo (scene/gizmo.js's updateGizmo) only ever draws move handles,
      // so force that mode rather than leaving it stuck on whatever a
      // previously-selected real object had active, which would otherwise
      // select the eye into showing no gizmo at all.
      if(!S.eyeSelected){ select(null); S.eyeSelected = true; S.gizmo = 'move'; renderRails(); }
      const e = eyeVec();
      const depthMode = ev.shiftKey;
      const plane = depthMode
        ? new THREE.Plane(new THREE.Vector3(0,1,0), -e.y)
        : new THREE.Plane(new THREE.Vector3(0,0,1), -e.z);
      const start = planeHit(ev, plane);
      if(start) eyeDrag = {plane, depthMode, start, origin:{...S.nudge}};
      requestRender();
      return;
    }
  }

  // The selected object's own safe area, ahead of the general scene pick
  // below — close, possibly overlapping geometry (a Text sitting in front
  // of an Image, say) must not be able to steal a click clearly aimed at
  // the thing that's already selected and gizmo'd.
  const selForSafeArea = getSel();
  if(ev.button===0 && gizmo.visible && selForSafeArea && S.gizmo==='move' &&
     selForSafeArea.type!=='frame' && selForSafeArea.type!=='recess' &&
     gizmoScreenDist(ev) < gizmoSafeRadiusPx()){
    const o = selForSafeArea;
    const depthMode = ev.shiftKey;
    const plane = depthMode
      ? new THREE.Plane(new THREE.Vector3(0,1,0), -o.pos.y)
      : new THREE.Plane(new THREE.Vector3(0,0,1), -o.pos.z);
    const start = planeHit(ev, plane);
    if(start) drag = {o, plane, depthMode, start, origin:{...o.pos}};
    return;
  }

  const hit = (ev.button===0) ? pick(ev) : null;
  if(hit && hit.obj){
    select(hit.obj.id);
    // In move mode the object body is also a grab handle; other modes just select.
    // A frame's (and a recess's) position is fixed, so neither is ever a grab handle.
    if(S.gizmo==='move' && hit.obj.type!=='frame' && hit.obj.type!=='recess'){
      const o = hit.obj;
      const depthMode = ev.shiftKey;
      const plane = depthMode
        ? new THREE.Plane(new THREE.Vector3(0,1,0), -o.pos.y)
        : new THREE.Plane(new THREE.Vector3(0,0,1), -o.pos.z);
      const start = planeHit(ev, plane);
      if(start) drag = {o, plane, depthMode, start, origin:{...o.pos}};
    }
    return;
  }
  // camFor(ev), not isOrbitLike() — Split's right pane is always flatCam
  // regardless of S.view, and clicking empty space there has no "orbit" to
  // start (a fixed camera doesn't rotate).
  // Orbiting is a camera move, not a click on the scene — unlike Flat/On-site,
  // where empty space is otherwise inert and a click there can only mean
  // "deselect", here it's also how you look around, and losing the
  // selection every time you nudge the view was more disruptive than useful.
  if(ev.button===0 && camFor(ev)===orbitCam){ orbiting = {x:ev.clientX, y:ev.clientY, mode:'rot'}; return; }
  if(ev.button!==0 && camFor(ev)===orbitCam){ orbiting = {x:ev.clientX, y:ev.clientY, mode:'pan'}; return; }
  if(ev.button===0) select(null);
}

function onMove(ev){
  if(gzDrag){ moveGizmo(ev, gzDrag); return; }
  if(eyeDrag){
    const p = planeHit(ev, eyeDrag.plane);
    if(p){
      S.nudge.x = eyeDrag.origin.x + (p.x - eyeDrag.start.x);
      if(eyeDrag.depthMode) S.nudge.z = eyeDrag.origin.z + (p.z - eyeDrag.start.z);
      else                  S.nudge.y = eyeDrag.origin.y + (p.y - eyeDrag.start.y);
      updateEyeHelpers();
      refreshInspectorValues();
      requestRender();
    }
    return;
  }
  if(drag){
    const p = planeHit(ev, drag.plane);
    if(p){
      drag.o.pos.x = drag.origin.x + (p.x - drag.start.x);
      if(drag.depthMode) drag.o.pos.z = drag.origin.z + (p.z - drag.start.z);
      else               drag.o.pos.y = drag.origin.y + (p.y - drag.start.y);
      captureKey(drag.o, 'pos');   // a plane drag moves, and only moves
      syncTransform(drag.o);
      refreshInspectorValues();
      requestRender();
    }
    return;
  }
  if(orbiting){
    const dx = ev.clientX-orbiting.x, dy = ev.clientY-orbiting.y;
    orbiting.x = ev.clientX; orbiting.y = ev.clientY;
    if(orbiting.mode==='rot'){
      orb.theta -= dx*0.006;
      orb.phi = clamp(orb.phi - dy*0.006, 0.03, Math.PI-0.03);
    }else{
      const k = orb.dist*0.0016;
      const right = new THREE.Vector3().setFromMatrixColumn(orbitCam.matrixWorld,0);
      const up    = new THREE.Vector3().setFromMatrixColumn(orbitCam.matrixWorld,1);
      orb.target.addScaledVector(right,-dx*k).addScaledVector(up, dy*k);
    }
    requestRender();
    return;
  }
  if(gizmo.visible){
    const gh = pickGizmo(ev);
    if(setHover(gh ? gh.node : null)) requestRender();
    ev.currentTarget.style.cursor = gh ? 'grab' : '';
  }
}
const endPtr = ev=>{
  if(drag || gzDrag){ renderRails(); pushHistory(); }
  // Not renderRails() — nudging never touches S.solved, so nothing about
  // the Calibration panel's own badge/fields needs a rebuild, just an undo
  // step, matching the existing Reset nudge button's own pushHistory().
  if(eyeDrag) pushHistory();
  drag = null; gzDrag = null; orbiting = null; eyeDrag = null;
  try{ ev.currentTarget.releasePointerCapture(ev.pointerId); }catch(e){}
};
/* Listeners attach to each pane's CONTAINER (#fitbox, #fitboxSplitR,
   #siteWrap), not the canvas inside it directly — every pane's own mask
   overlay SVG is a sibling of that canvas, on top of it (see index.html),
   and its point markers have pointer-events:auto so they can be grabbed.
   A click on one of those markers never reaches a listener bound to the
   canvas underneath; bound to the shared ancestor instead, it arrives via
   ordinary bubbling regardless of which of the two was actually hit. */
[$('#fitbox'), $('#siteWrap'), $('#fitboxSplitR'), $('#fitboxCornerR')].forEach(surf=>{
  surf.addEventListener('pointerdown', onDown);
  surf.addEventListener('pointermove', onMove);
  surf.addEventListener('pointerup', endPtr);
  surf.addEventListener('pointercancel', endPtr);
  surf.addEventListener('contextmenu', e=>e.preventDefault());
});
$('#fitbox').addEventListener('wheel', ev=>{
  if(camFor(ev)===orbitCam){
    ev.preventDefault();
    orb.dist = clamp(orb.dist * Math.pow(1.0016, ev.deltaY), 0.4, 8000);
    requestRender();
    return;
  }
  // Standalone Flat only — scales #fitbox itself (ui/flat-view.js), exactly
  // as On-site scales #siteWrap. Anchored to the POINTER, so whatever you
  // point at stays under the cursor; the old frustum zoom always magnified
  // about the screen's centre, so the thing you were zooming toward slid
  // away from you. Split's right pane and On-site both also render through
  // flatCam but aren't interactive here.
  if(S.view==='flat'){
    ev.preventDefault();
    setFlatZoom((S.flatZoom || 1) * Math.pow(1.0016, -ev.deltaY),
                { x: ev.clientX, y: ev.clientY });
    // The watermark and the mask overlay are CHILDREN of #fitbox, so they are
    // carried by the same transform and need no repainting of their own —
    // which is what the old frustum crop could never give them. Still emitted
    // for anything else listening on the framing.
    emit('view:zoomed');
  }
}, {passive:false});

/* Flat: MIDDLE-drag pans, exactly as On-site does below — never the left
   button, which must not be mistaken for dragging an object. Flat only ever
   had a wheel handler writing S.flatZoom, so once you were zoomed in there
   was no way to move around and no way back.

   This used to claim space-or-middle. The space half never actually worked
   here: the key was only ever captured while On-site, so in Flat it fell
   through to play/pause and the flag this tested stayed false. Middle-drag
   was doing all the work, and now says so. */
/* Split's right half is a Flat pane too (ui/flat-view.js's flatTarget), so it
   zooms under the wheel and pans on a middle or right drag exactly as
   standalone Flat does. */
$('#fitboxSplitR').addEventListener('wheel', ev=>{
  if(flatTarget() !== ev.currentTarget) return;
  ev.preventDefault();
  setFlatZoom((S.flatZoom || 1) * Math.pow(1.0016, -ev.deltaY), { x: ev.clientX, y: ev.clientY });
  emit('view:zoomed');
}, {passive:false});
let flatPanning = null;
$('#fitboxSplitR').addEventListener('pointerdown', ev=>{
  if(flatTarget() !== ev.currentTarget) return;
  if(ev.button !== 1 && ev.button !== 2) return;
  ev.preventDefault(); ev.stopPropagation();
  flatPanning = { x: ev.clientX, y: ev.clientY };
  try{ ev.currentTarget.setPointerCapture(ev.pointerId); }catch(_){}
}, true);
$('#fitbox').addEventListener('pointerdown', ev=>{
  if(S.view!=='flat') return;
  /* Middle OR right, not just middle — middle-drag alone is a gesture most
     people never find, and right-drag already means "pan" in Orbit, so it is
     the one to match. Left stays out of it here for the reason it always did:
     in Flat you can still select and drag objects, and a left-drag that
     sometimes moves the artwork and sometimes moves the view is worse than
     one that needs a different button. */
  if(ev.button !== 1 && ev.button !== 2) return;
  ev.preventDefault(); ev.stopPropagation();
  flatPanning = { x: ev.clientX, y: ev.clientY };
  try{ $('#fitbox').setPointerCapture(ev.pointerId); }catch(_){}
}, true);
addEventListener('pointermove', ev=>{
  if(!flatPanning) return;
  panFlatByPixels(ev.clientX - flatPanning.x, ev.clientY - flatPanning.y);
  flatPanning = { x: ev.clientX, y: ev.clientY };
  emit('view:zoomed');
});
addEventListener('pointerup', ()=>{ flatPanning = null; });

/* On-site: wheel (or pinch) zooms about the pointer; shift-scroll, right-drag
   and middle-drag all pan. Panning is never the plain left button, so it can
   never be mistaken for dragging an object on the building.

   THREE ways to pan because one of them has to work on whatever is under your
   hands. Middle-drag alone was the state of things briefly and it is useless
   on a trackpad, which has no middle button — so panning the photograph, the
   thing you do constantly while placing a build against a wall, was simply
   unavailable to anyone not using a mouse.

   Right-drag is the same word this app already uses for pan in Orbit (see
   VIEW_HINT in ui/chrome.js), and a trackpad does it with a two-finger
   click-drag. Shift-scroll is the gesture a trackpad reaches for first.
   Both deltas are read, because which axis the OS reports for a shifted
   scroll differs between them — macOS turns it into deltaX, Windows may
   leave it on deltaY, and honouring both means the gesture works either
   way instead of only where it was tested. */
let sitePanning = null;
/* THE HOST THESE FOUR BELONG TO MOVES. Zooming, panning and click-away-to-
   deselect are properties of the box the mockup sits in, not of the mockup
   itself, so they were bound to #viewSite — which in Split is not where it
   sits. Bound to #stage instead and gated on the host of the moment, so the
   same three gestures work in the pane without a second copy of any of them.

   The containment test is what keeps them off the orbit pane: in Split a click
   on #cv is inside #stage and outside #siteWrap, which is exactly the shape of
   "empty space beside the building" that the last of these deselects on. */
const siteHost = () => $('#siteWrap').parentElement;
const inSiteHost = ev => isSiteLike() && !!siteHost() && siteHost().contains(ev.target);
$('#stage').addEventListener('wheel', ev=>{
  if(!inSiteHost(ev)) return;
  ev.preventDefault();
  /* Whichever axes arrive, both are honoured — a shifted scroll is reported
     as deltaX on some systems and deltaY on others, and a trackpad swipe can
     carry both at once. Subtracted so the photo follows the fingers rather
     than running away from them. */
  if(ev.shiftKey){
    S.sitePan = { x: S.sitePan.x - ev.deltaX, y: S.sitePan.y - ev.deltaY };
    applySiteTransform();
    return;
  }
  setSiteZoom((S.siteZoom||1) * Math.pow(0.9985, ev.deltaY), {x:ev.clientX, y:ev.clientY});
}, {passive:false});
/* A right-drag must not also open the menu. #viewSite is not one of the
   surfaces that already suppress it (see the list further up — those are the
   render panes, this is the photo wrapper around one). */
$('#stage').addEventListener('contextmenu', ev=>{ if(inSiteHost(ev)) ev.preventDefault(); });

$('#stage').addEventListener('pointerdown', ev=>{
  if(!inSiteHost(ev)) return;
  if(ev.button !== 1 && ev.button !== 2) return;   // middle or right — never left
  ev.preventDefault(); ev.stopPropagation();
  sitePanning = {x:ev.clientX, y:ev.clientY, px:S.sitePan.x, py:S.sitePan.y};
  try{ $('#stage').setPointerCapture(ev.pointerId); }catch(_){}
}, true);
/* #siteWrap shrink-wraps to the photo at its current zoom (see app.css),
   so on-site genuinely has dead space around it whenever the photo's own
   aspect doesn't fill the stage — a click there never reaches #siteWrap's
   own onDown (below), so unlike Orbit/Flat, where empty space is the whole
   canvas, there was no way to deselect by clicking away from the building.
   Plain left-click only — middle-click already means "start panning" and is
   claimed by the capture-phase listener above before this ever runs. */
$('#stage').addEventListener('pointerdown', ev=>{
  if(!inSiteHost(ev) || ev.button!==0 || mask.active) return;
  if(ev.target.closest('#siteWrap')) return;
  select(null);
});
addEventListener('pointermove', ev=>{
  if(!sitePanning) return;
  S.sitePan = { x: sitePanning.px + (ev.clientX - sitePanning.x),
                y: sitePanning.py + (ev.clientY - sitePanning.y) };
  applySiteTransform();
});
addEventListener('pointerup', ()=>{ sitePanning = null; });

addEventListener('keydown', ev=>{
  const t = ev.target.tagName;
  /* isContentEditable, not a tagName check — the Text Area editor is a
     <div contenteditable>, so its tagName ('DIV') sailed straight through
     this guard and every letter you typed there doubled as a viewport hotkey
     (g/r/s swapped the gizmo mode, f framed the view, Backspace deleted the
     selected object...). */
  if(typingTarget(ev.target)) return;
  /* And the per-key half of the same guard: a number/colour field keeps the
     keys that edit it (Backspace, Delete, the arrows) while still letting a
     space through to the timeline below. See state/store.js's fieldOwnsKey. */
  if(fieldOwnsKey(ev.target, ev.key)) return;
  /* Space plays and pauses in EVERY view, On-site included. It used to be
     held there to pan the photograph instead, on the reasoning that
     positioning against a real building had the better claim on the key —
     but that made the one shortcut people use constantly die silently
     whenever they happened to be on that tab, with nothing to say why.

     Panning loses nothing: middle-drag already pans and always did, needing
     no key at all (see #viewSite's own pointerdown below). The only thing
     that goes is the hold-Space-and-left-drag variant of a gesture that has
     another way in. Calibrate still has no timeline to run. */
  if(ev.code==='Space'){
    /* Licensed, like every other way into playback. This binding is global
       rather than owned by the dock, so gating the dock alone would have left
       Space running the timeline for someone who cannot see it. Silent: a
       key with no visible control behind it has nothing to explain, and a
       toast on every stray Space press would be its own bug. */
    if(S.stage!=='calib' && canUseMotion().ok){ ev.preventDefault(); togglePlay(); }
    return;
  }
  /* Delete belongs to whichever selection you last made. With keyframes
     selected the timeline owns it — its own handler does the deleting, and
     this steps aside rather than removing the whole object out from under
     someone who meant to remove one diamond. This listener is registered at
     module load and the timeline's inside initTimeline, so this one runs
     FIRST: without the check the object was already gone by the time the
     timeline was consulted, whatever guard the timeline itself applied. */
  if(ev.key==='Delete'||ev.key==='Backspace'){
    if(hasKeySelection()) return;
    ev.preventDefault(); deleteSel();
  }
  /* Same rule Delete follows above: with keyframes selected the timeline owns
     Ctrl+D and duplicates those instead, so this stands aside rather than
     duplicating the whole object. */
  if((ev.metaKey||ev.ctrlKey) && ev.key.toLowerCase()==='d'){
    if(hasKeySelection()) return;
    ev.preventDefault(); duplicateSel();
  }
  if((ev.metaKey||ev.ctrlKey) && ev.key.toLowerCase()==='z'){ ev.preventDefault(); ev.shiftKey?redo():undo(); }
  if((ev.metaKey||ev.ctrlKey) && ev.key.toLowerCase()==='y'){ ev.preventDefault(); redo(); }
  if(ev.key==='f'||ev.key==='F'){ getSel()?frameSelection():frameOrbit(); requestRender(); }
  const nudgeKey = {ArrowLeft:[-1,0],ArrowRight:[1,0],ArrowUp:[0,1],ArrowDown:[0,-1]}[ev.key];
  if(nudgeKey && getSel() && getSel().type!=='frame' && getSel().type!=='recess'){
    ev.preventDefault();
    const o = getSel(), step = ev.shiftKey ? .01 : .05;
    /* Arrow keys move along the WORLD axes, so a linked object's step goes
       through its master's frame like a handle drag does. */
    const dl = worldDeltaToLocal(o, ev.altKey
      ? new THREE.Vector3(0, 0, nudgeKey[1]*step)
      : new THREE.Vector3(nudgeKey[0]*step, nudgeKey[1]*step, 0));
    o.pos.x += dl.x; o.pos.y += dl.y; o.pos.z += dl.z;
    captureKey(o, 'pos');        // arrow keys nudge position, nothing else
    syncTransform(o); refreshInspectorValues(); requestRender(); pushHistorySoon();
  }
  if(ev.key==='['||ev.key===']'){
    ev.preventDefault();
    S.gizmoSize = clamp(S.gizmoSize + (ev.key===']'?.15:-.15), .5, 3);
    renderRails(); requestRender();
  }
  const gk = {v:'select', g:'move', r:'rotate', s:'scale'}[ev.key.toLowerCase()];
  if(gk && !ev.metaKey && !ev.ctrlKey && !ev.altKey){ ev.preventDefault(); setGizmoMode(gk); }
});
